import {
  Commitment,
  Connection,
  PublicKey,
  Keypair,
  Transaction,
  SystemProgram,
} from "@solana/web3.js";
import { BigNumber, utils } from "ethers";
import * as anchor from "@coral-xyz/anchor";
import { CCTPMessageTransmitterIdl } from "./idls/message-transmitter";
import {
  CCTPDomain,
  decodeMessage,
  findProgramAddress,
  getNetworkConfigs,
  getPrograms,
  getSolanaConnection,
  getSolanaTokenAccount,
  hexToBytes,
  isNonceUsedOnEVM,
  isNonceUsedOnSolana,
  simulateTransactionWithoutSigning,
  waitForMessage,
} from "../utils";
import { TOKEN_PROGRAM_ID, createApproveInstruction } from "@solana/spl-token";
import {
  ApproveUSDCOnSolanaOptions,
  BurnUSDCOnSolanaOptions,
  CCTPSdkConfigsSet,
  CCTPSdkNetworkConfigs,
  IsTransferCompletedOptions,
  MintUSDCOnSolanaOptions,
} from "../interface";
import { Wallet } from "@coral-xyz/anchor/dist/cjs/provider";

function evmAddressToBytes32(address: string): string {
  return `0x000000000000000000000000${address.replace("0x", "")}`;
}

function evmAddressToBase58PublicKey(addressHex: string): PublicKey {
  return new PublicKey(utils.arrayify(evmAddressToBytes32(addressHex)));
}

function getDepositForBurnPdas(
  {
    messageTransmitterProgram,
    tokenMessengerMinterProgram,
  }: ReturnType<typeof getPrograms>,
  usdcAddress: PublicKey,
  destinationDomain: Number
) {
  const messageTransmitterAccount = findProgramAddress(
    "message_transmitter",
    messageTransmitterProgram.programId
  );
  const tokenMessengerAccount = findProgramAddress(
    "token_messenger",
    tokenMessengerMinterProgram.programId
  );
  const tokenMinterAccount = findProgramAddress(
    "token_minter",
    tokenMessengerMinterProgram.programId
  );
  const localToken = findProgramAddress(
    "local_token",
    tokenMessengerMinterProgram.programId,
    [usdcAddress]
  );
  const remoteTokenMessengerKey = findProgramAddress(
    "remote_token_messenger",
    tokenMessengerMinterProgram.programId,
    [destinationDomain.toString()]
  );
  const authorityPda = findProgramAddress(
    "sender_authority",
    tokenMessengerMinterProgram.programId
  );

  return {
    messageTransmitterAccount,
    tokenMessengerAccount,
    tokenMinterAccount,
    localToken,
    remoteTokenMessengerKey,
    authorityPda,
  };
}

async function getReceiveMessagePdas(
  {
    messageTransmitterProgram,
    tokenMessengerMinterProgram,
  }: ReturnType<typeof getPrograms>,
  connection: Connection,
  networkConfigs: CCTPSdkNetworkConfigs,
  solUsdcAddress: PublicKey,
  sourceUsdcAddress: string,
  sourceDomain: number,
  nonce: string
) {
  const tokenMessengerAccount = findProgramAddress(
    "token_messenger",
    tokenMessengerMinterProgram.programId
  );
  const messageTransmitterAccount = findProgramAddress(
    "message_transmitter",
    messageTransmitterProgram.programId
  );
  const tokenMinterAccount = findProgramAddress(
    "token_minter",
    tokenMessengerMinterProgram.programId
  );
  const localToken = findProgramAddress(
    "local_token",
    tokenMessengerMinterProgram.programId,
    [solUsdcAddress]
  );
  const remoteTokenMessengerKey = findProgramAddress(
    "remote_token_messenger",
    tokenMessengerMinterProgram.programId,
    [sourceDomain.toString()]
  );
  const remoteTokenKey = new PublicKey(hexToBytes(sourceUsdcAddress));
  const tokenPair = findProgramAddress(
    "token_pair",
    tokenMessengerMinterProgram.programId,
    [sourceDomain.toString(), remoteTokenKey]
  );
  const custodyTokenAccount = findProgramAddress(
    "custody",
    tokenMessengerMinterProgram.programId,
    [solUsdcAddress]
  );
  const authorityPda = findProgramAddress(
    "message_transmitter_authority",
    messageTransmitterProgram.programId,
    [tokenMessengerMinterProgram.programId]
  ).publicKey;
  const tokenMessengerEventAuthority = findProgramAddress(
    "__event_authority",
    tokenMessengerMinterProgram.programId
  );

  const { messageTransmitterProgram: messageTransmitterProgramWithoutSigner } =
    getPrograms({
      messageTransmitterProgramPubkey: new PublicKey(
        networkConfigs.cctpMessageTransmitterContractAddress
      ),
      tokenMessengerMinterProgramPubkey: new PublicKey(
        networkConfigs.cctpMessengerContractAddress
      ),
    });

  const usedNoncesTx = await messageTransmitterProgramWithoutSigner.methods
    .getNoncePda({
      nonce: new anchor.BN(nonce),
      sourceDomain: Number(sourceDomain),
    })
    .accounts({
      messageTransmitter: messageTransmitterAccount.publicKey,
    })
    .transaction();

  const usedNonces: PublicKey = await simulateTransactionWithoutSigning({
    connection,
    messageTransmitterProgram: messageTransmitterProgramWithoutSigner,
    idl: CCTPMessageTransmitterIdl,
    instructionName: "getNoncePda",
    tx: usedNoncesTx,
  });

  return {
    messageTransmitterAccount,
    tokenMessengerAccount,
    tokenMinterAccount,
    localToken,
    remoteTokenMessengerKey,
    remoteTokenKey,
    tokenPair,
    custodyTokenAccount,
    authorityPda,
    tokenMessengerEventAuthority,
    usedNonces,
  };
}

async function sendBurnTransaction(options: {
  connection: Connection;
  wallet: Wallet;
  userTokenAccount: PublicKey;
  usdcTokenAddress: PublicKey;
  messageTransmitterProgramPubkey: PublicKey;
  tokenMessengerMinterProgramPubkey: PublicKey;
  amount: bigint;
  destinationAddress: string;
  destinationDomain: CCTPDomain;
  commitment?: Commitment;
}) {
  const {
    connection,
    wallet,
    userTokenAccount,
    usdcTokenAddress,
    messageTransmitterProgramPubkey,
    tokenMessengerMinterProgramPubkey,
    amount,
    destinationAddress,
    destinationDomain,
    commitment,
  } = options;

  const { messageTransmitterProgram, tokenMessengerMinterProgram } =
    getPrograms({
      wallet,
      connection,
      messageTransmitterProgramPubkey,
      tokenMessengerMinterProgramPubkey,
    });
  const pdas = getDepositForBurnPdas(
    { messageTransmitterProgram, tokenMessengerMinterProgram },
    usdcTokenAddress,
    destinationDomain
  );
  const messageSentEventAccountKeypair = Keypair.generate();
  const mintRecipient = evmAddressToBase58PublicKey(destinationAddress);

  console.log("Calling depositForBurn with parameters: ");
  console.log("sender", wallet.publicKey.toBase58());
  console.log("amount:", amount.toString());
  console.log("destinationDomain:", destinationDomain);
  console.log("mintRecipient (hex):", destinationAddress);
  console.log("mintRecipient (bytes52):", mintRecipient.toString());
  console.log(
    "remoteTokenMessenger (bytes52):",
    pdas.remoteTokenMessengerKey.publicKey.toString()
  );
  console.log(
    "burnToken:",
    {
      amount,
      destinationDomain,
      mintRecipient,
    },
    {
      owner: wallet.publicKey,
      eventRentPayer: wallet.publicKey,
      senderAuthorityPda: pdas.authorityPda.publicKey,
      burnTokenAccount: userTokenAccount,
      messageTransmitter: pdas.messageTransmitterAccount.publicKey,
      tokenMessenger: pdas.tokenMessengerAccount.publicKey,
      remoteTokenMessenger: pdas.remoteTokenMessengerKey.publicKey,
      tokenMinter: pdas.tokenMinterAccount.publicKey,
      localToken: pdas.localToken.publicKey,
      burnTokenMint: usdcTokenAddress,
      messageTransmitterProgram: messageTransmitterProgram.programId,
      tokenMessengerMinterProgram: tokenMessengerMinterProgram.programId,
      messageSentEventData: messageSentEventAccountKeypair.publicKey,
      tokenProgram: TOKEN_PROGRAM_ID,
    }
  );

  // Call depositForBurn
  const depositForBurnTxHash = await tokenMessengerMinterProgram.methods
    .depositForBurn({
      amount: new anchor.BN(amount),
      destinationDomain,
      mintRecipient,
    })
    // eventAuthority and program accounts are implicitly added by Anchor
    .accounts({
      owner: wallet.publicKey,
      eventRentPayer: wallet.publicKey,
      senderAuthorityPda: pdas.authorityPda.publicKey,
      burnTokenAccount: userTokenAccount,
      messageTransmitter: pdas.messageTransmitterAccount.publicKey,
      tokenMessenger: pdas.tokenMessengerAccount.publicKey,
      remoteTokenMessenger: pdas.remoteTokenMessengerKey.publicKey,
      tokenMinter: pdas.tokenMinterAccount.publicKey,
      localToken: pdas.localToken.publicKey,
      burnTokenMint: usdcTokenAddress,
      messageTransmitterProgram: messageTransmitterProgram.programId,
      tokenMessengerMinterProgram: tokenMessengerMinterProgram.programId,
      messageSentEventData: messageSentEventAccountKeypair.publicKey,
      tokenProgram: TOKEN_PROGRAM_ID,
    })
    // messageSentEventAccountKeypair must be a signer so the MessageTransmitter program can take control of it and write to it.
    // provider.wallet is also an implicit signer
    .signers([messageSentEventAccountKeypair])
    .rpc({ commitment });

  console.log("\ndepositForBurn Tx: ", depositForBurnTxHash);

  return depositForBurnTxHash;
}

async function sendMintTransaction(options: {
  connection: Connection;
  wallet: Wallet;
  userTokenAccount: PublicKey;
  usdcTokenAddress: PublicKey;
  messageTransmitterProgramPubkey: PublicKey;
  tokenMessengerMinterProgramPubkey: PublicKey;
  messageBytes: string;
  attestationSignature: string;
  configsSet: CCTPSdkConfigsSet;
  commitment?: Commitment;
}) {
  const {
    connection,
    wallet,
    userTokenAccount,
    usdcTokenAddress,
    messageTransmitterProgramPubkey,
    tokenMessengerMinterProgramPubkey,
    messageBytes,
    attestationSignature,
    configsSet,
    commitment,
  } = options;

  const { messageTransmitterProgram, tokenMessengerMinterProgram } =
    getPrograms({
      wallet,
      connection,
      messageTransmitterProgramPubkey,
      tokenMessengerMinterProgramPubkey,
    });

  // Init needed variables
  const destinationUSDCAddress = new PublicKey(usdcTokenAddress);
  const { nonce, sourceDomain, destinationDomain } =
    decodeMessage(messageBytes);
  const sourceNetworkConfigs = getNetworkConfigs(
    configsSet,
    Number(sourceDomain)
  );
  const sourceTokenAddress = sourceNetworkConfigs.usdcContractAddress;

  // Get PDAs
  const destinationNetworkConfigs = getNetworkConfigs(
    configsSet,
    Number(destinationDomain)
  );
  const pdas = await getReceiveMessagePdas(
    { messageTransmitterProgram, tokenMessengerMinterProgram },
    connection,
    destinationNetworkConfigs,
    destinationUSDCAddress,
    sourceTokenAddress,
    sourceDomain,
    nonce
  );

  // accountMetas list to pass to remainingAccounts
  const accountMetas: any[] = [];
  accountMetas.push({
    isSigner: false,
    isWritable: false,
    pubkey: pdas.tokenMessengerAccount.publicKey,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: false,
    pubkey: pdas.remoteTokenMessengerKey.publicKey,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: true,
    pubkey: pdas.tokenMinterAccount.publicKey,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: true,
    pubkey: pdas.localToken.publicKey,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: false,
    pubkey: pdas.tokenPair.publicKey,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: true,
    pubkey: userTokenAccount,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: true,
    pubkey: pdas.custodyTokenAccount.publicKey,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: false,
    pubkey: TOKEN_PROGRAM_ID,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: false,
    pubkey: pdas.tokenMessengerEventAuthority.publicKey,
  });
  accountMetas.push({
    isSigner: false,
    isWritable: false,
    pubkey: tokenMessengerMinterProgram.programId,
  });

  const receiveMessageTxHash = await messageTransmitterProgram.methods
    .receiveMessage({
      message: Buffer.from(messageBytes.replace("0x", ""), "hex"),
      attestation: Buffer.from(attestationSignature.replace("0x", ""), "hex"),
    })
    .accounts({
      payer: wallet.publicKey,
      caller: wallet.publicKey,
      authorityPda: pdas.authorityPda,
      messageTransmitter: pdas.messageTransmitterAccount.publicKey,
      usedNonces: pdas.usedNonces,
      receiver: tokenMessengerMinterProgram.programId,
      systemProgram: SystemProgram.programId,
    })
    .remainingAccounts(accountMetas)
    .rpc({ commitment });

  console.log("\n\nreceiveMessage Tx: ", receiveMessageTxHash);

  return receiveMessageTxHash;
}

async function getSolanaConfigs(options: {
  wallet: Wallet;
  configsSet: CCTPSdkConfigsSet;
}) {
  const { wallet, configsSet } = options;
  const networkConfigs = getNetworkConfigs(configsSet, CCTPDomain.Solana);
  const connection = getSolanaConnection(networkConfigs.rpc);
  const userTokenAccount = await getSolanaTokenAccount({
    connection,
    publicKey: wallet.publicKey,
    tokenMintAddress: networkConfigs.usdcContractAddress,
  });

  return {
    userTokenAccount,
    connection,
    networkConfigs,
  };
}

async function sendTransaction(
  connection: Connection,
  transaction: Transaction
) {
  const rawTransaction = transaction.serialize();
  const signature = await connection.sendRawTransaction(rawTransaction, {
    skipPreflight: true,
    maxRetries: 5,
  });

  return signature;
}

export async function burnUSDCOnSolana(options: BurnUSDCOnSolanaOptions) {
  const { wallet, destinationDomain, destinationAddress, amount, configsSet } =
    options;

  const { connection, networkConfigs, userTokenAccount } =
    await getSolanaConfigs({ wallet, configsSet });
  const amountBigInt = BigNumber.from(amount).toBigInt();
  const txHash = await sendBurnTransaction({
    connection,
    wallet,
    userTokenAccount,
    usdcTokenAddress: new PublicKey(networkConfigs.usdcContractAddress),
    messageTransmitterProgramPubkey: new PublicKey(
      networkConfigs.cctpMessageTransmitterContractAddress
    ),
    tokenMessengerMinterProgramPubkey: new PublicKey(
      networkConfigs.cctpMessengerContractAddress
    ),
    amount: amountBigInt,
    destinationAddress,
    destinationDomain,
    commitment: "confirmed",
  });

  return { hash: txHash };
}

export async function mintUSDCOnSolana(options: MintUSDCOnSolanaOptions) {
  const { wallet, messageBytes, attestationSignature, configsSet } = options;
  const { connection, networkConfigs, userTokenAccount } =
    await getSolanaConfigs({ wallet, configsSet });

  const txHash = await sendMintTransaction({
    connection,
    wallet,
    userTokenAccount,
    usdcTokenAddress: new PublicKey(networkConfigs.usdcContractAddress),
    messageTransmitterProgramPubkey: new PublicKey(
      networkConfigs.cctpMessageTransmitterContractAddress
    ),
    tokenMessengerMinterProgramPubkey: new PublicKey(
      networkConfigs.cctpMessengerContractAddress
    ),
    messageBytes,
    attestationSignature,
    configsSet,
    commitment: "confirmed",
  });

  return { hash: txHash };
}

export async function approveUSDCOnSolana(options: ApproveUSDCOnSolanaOptions) {
  const { wallet, amount, configsSet } = options;
  const { connection, networkConfigs, userTokenAccount } =
    await getSolanaConfigs({ wallet, configsSet });

  const amountBigInt = BigNumber.from(amount).toBigInt();
  const approvalIx = createApproveInstruction(
    userTokenAccount,
    new PublicKey(networkConfigs.cctpMessengerContractAddress),
    wallet.publicKey,
    amountBigInt
  );
  const transaction = new Transaction().add(approvalIx);

  const { blockhash } = await connection.getLatestBlockhash("confirmed");

  transaction.recentBlockhash = blockhash;
  transaction.feePayer = wallet.publicKey;

  const signedTransaction = await wallet.signTransaction(transaction);
  const txHash = await sendTransaction(connection, signedTransaction);

  return { hash: txHash };
}

export async function isTransferCompletedOnSolana(
  options: IsTransferCompletedOptions
) {
  const { sourceDomain, burnTxHash, configsSet } = options;
  const { message } = await waitForMessage({
    apiHost: configsSet.irisApiHost,
    domain: sourceDomain,
    transactionHash: burnTxHash,
  });
  const { destinationDomain, nonce } = decodeMessage(message);
  const destinationNetworkConfigs = getNetworkConfigs(
    configsSet,
    destinationDomain
  );

  const isNonceUsed = await isNonceUsedOnEVM({
    networkConfigs: destinationNetworkConfigs,
    sourceDomain,
    nonce: Number(nonce),
  });

  return isNonceUsed;
}
